import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://ops.maritronix.in/api";

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const logoutAndRedirect = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user_data");
  window.location.href = "/login";
};

// On 401, try a token refresh once before giving up so users aren't logged
// out every time the (shorter) access token expires.
let refreshPromise = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        logoutAndRedirect();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${API_BASE_URL}/token/refresh/`, { refresh: refreshToken })
            .finally(() => {
              refreshPromise = null;
            });
        }
        const { data } = await refreshPromise;
        localStorage.setItem("access_token", data.access);
        originalRequest.headers.Authorization = `Bearer ${data.access}`;
        return api(originalRequest);
      } catch (refreshError) {
        logoutAndRedirect();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export const authAPI = {
  login: (credentials) => api.post("/auth/login/", credentials),
};

export const userAPI = {
  getUsers: () => api.get("/users/"),
  createUser: (userData) => api.post("/users/", userData),
  updateUser: (id, userData) => api.put(`/users/${id}/`, userData),
  deleteUser: (id) => api.delete(`/users/${id}/`),
  changePassword: (data) => api.post("/users/change_password/", data),
};

export const roleAPI = {
  getRoles: () => api.get("/roles/"),
  createRole: (roleData) => api.post("/roles/", roleData),
};

export const salesAPI = {
  getSales: () => api.get("/sales/tracker/"),
  createSale: (saleData) => api.post("/sales/tracker/", saleData),
  updateSale: (id, saleData) => api.patch(`/sales/tracker/${id}/`, saleData),
  deleteSale: (id) => api.delete(`/sales/tracker/${id}/`),
  getStats: () => api.get("/sales/tracker/stats/"),
  getRecent: () => api.get("/sales/tracker/recent/"),
  exportExcel: () =>
    api.get("/sales/tracker/export/", { responseType: "blob" }),
};

// Add these to your existing api.js file

export const permissionAPI = {
  getPermissions: () => api.get("/permissions/"),
  createPermission: (permissionData) =>
    api.post("/permissions/", permissionData),
  updatePermission: (id, permissionData) =>
    api.put(`/permissions/${id}/`, permissionData),
  deletePermission: (id) => api.delete(`/permissions/${id}/`),
  getMyPermissions: () => api.get("/my-permissions/"),
};

export const moduleAPI = {
  getModules: () => api.get("/modules/"),
  createModule: (moduleData) => api.post("/modules/", moduleData),
  updateModule: (id, moduleData) => api.put(`/modules/${id}/`, moduleData),
  deleteModule: (id) => api.delete(`/modules/${id}/`),
};
// Make sure you have these exports
export const purchaseAPI = {
  getPurchases: () => api.get("/purchase/tracker/"),
  createPurchase: (purchaseData) =>
    api.post("/purchase/tracker/", purchaseData),
  updatePurchase: (id, purchaseData) =>
    api.put(`/purchase/tracker/${id}/`, purchaseData),
  deletePurchase: (id) => api.delete(`/purchase/tracker/${id}/`),
  getStats: () => api.get("/purchase/tracker/stats/"),
  getRecent: () => api.get("/purchase/tracker/recent/"),
  exportExcel: () =>
    api.get("/purchase/tracker/export/", { responseType: "blob" }),
};

// services/api.js (append)
export const orderAPI = {
  getOrders: () => api.get("/order/tracker/"),
  createOrder: (data) => api.post("/order/tracker/", data),
  updateOrder: (id, data) => api.put(`/order/tracker/${id}/`, data),
  deleteOrder: (id) => api.delete(`/order/tracker/${id}/`),
  getStats: () => api.get("/order/tracker/stats/"),
  getRecent: () => api.get("/order/tracker/recent/"),
  exportExcel: () =>
    api.get("/order/tracker/export/", { responseType: "blob" }),
  getNextSrNo: () => api.get("/order/tracker/next_sr_no/"),
};

export const auditAPI = {
  getLogs: () => api.get("/audit/logs/"),
};
export const currencyAPI = {
  getCurrencies: () => api.get("/currency/"),
  createCurrency: (data) => api.post("/currency/", data),
  deleteCurrency: (id) => api.delete(`/currency/${id}/`),
};
export const vendorAPI = {
  getVendors: () => api.get("/vendors/"),
  createVendor: (data) => api.post("/vendors/", data),
  updateVendor: (id, data) => api.put(`/vendors/${id}/`, data),
  deleteVendor: (id) => api.delete(`/vendors/${id}/`),
  exportExcel: () => api.get("/vendors/export/", { responseType: "blob" }),
  getNextSrNo: () => api.get("/vendors/next_sr_no/"),
};

export const reportsAPI = {
  emailReport: (formData) =>
    api.post("/reports/email/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};
